/**
 * Copyright (C) 2026 Jugendstil, SLU - All Rights Reserved
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited. Proprietary and confidential.
 */
import { register } from '../registry.js';

/** How much of the visible width one press moves, leaving a tile of overlap so nothing is jumped over. */
const PAGE_FRACTION = 0.8;

/** Sub-pixel slack. A scroller at its end is rarely at exactly 0 or exactly the maximum. */
const EPSILON = 1;

/**
 * The two arrows that let a mouse reach the rest of a carousel.
 *
 * A carousel is a scroll container with its scrollbar hidden, which is right on a phone -- the
 * gutter leaves the next tile peeking and a thumb already knows what to do with that -- and leaves
 * a desktop mouse with nothing. A wheel scrolls vertically, the scrollbar it would otherwise drag
 * is not drawn, and shift+wheel is not something anyone discovers. The tiles past the fold were
 * reachable only by trackpad.
 *
 * So the arrows are pointer-only, in CSS rather than here: `@media (hover: hover) and
 * (pointer: fine)` is what decides whether they are drawn, the same query the tab bar and the bar
 * buttons use to keep hover off a touch screen. This file assumes nothing about which it is.
 *
 * Each arrow hides itself when there is nothing that way, and both hide when nothing overflows at
 * all -- a wide desktop often fits the whole row. A control that does nothing is worse than no
 * control: it is the page claiming there is more to see.
 *
 * Nothing here creates markup. The frame and its two buttons are in the template, so a page with
 * no script has a carousel that still scrolls by touch and trackpad exactly as it did, and the
 * only thing missing is the pair of arrows that would not work anyway.
 */
function scrollerOf(frame: Element): HTMLElement | null {
    const scroller = frame.querySelector('.wp-carousel');

    return scroller instanceof HTMLElement ? scroller : null;
}

/**
 * Shows each arrow only while it has somewhere to go.
 *
 * `hidden` rather than `disabled`: a disabled button is still a tab stop announcing itself as a
 * control that cannot be used, and at the end of a carousel there is no control, not a broken one.
 */
export function paintFrame(frame: Element): void {
    const scroller = scrollerOf(frame);
    if (scroller === null) {
        return;
    }

    const overflow = scroller.scrollWidth - scroller.clientWidth;
    const atStart = scroller.scrollLeft <= EPSILON;
    const atEnd = scroller.scrollLeft >= overflow - EPSILON;

    frame.querySelectorAll('[data-wp-carousel-scroll]').forEach((control) => {
        if (!(control instanceof HTMLElement)) {
            return;
        }

        const forwards = control.getAttribute('data-wp-carousel-scroll') !== 'previous';
        const exhausted = forwards ? atEnd : atStart;

        control.hidden = overflow <= EPSILON || exhausted;
    });
}

export function paint(root: ParentNode): void {
    root.querySelectorAll('.wp-carousel-frame').forEach((frame) => {
        paintFrame(frame);

        /* Per element and not delegated, because `scroll` does not bubble. Re-adding the same
           named function to the same element is a no-op, which is what keeps a repaint -- after a
           Turbo swap, or a `wp:paint` -- from stacking listeners. */
        const scroller = scrollerOf(frame);
        scroller?.addEventListener('scroll', () => paintFrame(frame), { passive: true });
    });
}

export function listen(): void {
    document.addEventListener('click', (event) => {
        const target = event.target;
        if (!(target instanceof Element)) {
            return;
        }

        const control = target.closest('[data-wp-carousel-scroll]');
        const frame = control?.closest('.wp-carousel-frame');
        if (control === null || frame == null) {
            return;
        }

        const scroller = scrollerOf(frame);
        if (scroller === null) {
            return;
        }

        const forwards = control.getAttribute('data-wp-carousel-scroll') !== 'previous';
        const distance = scroller.clientWidth * PAGE_FRACTION;

        /* `scrollBy` and not a tile count: the tiles are not all one width -- the dashboard mixes
           three card types in one row -- so a page of the viewport is the only measure that holds.
           Snapping then settles it on a tile edge, which is what `scroll-snap-type` is for. */
        scroller.scrollBy({ behavior: 'smooth', left: forwards ? distance : -distance });
    });

    /* A resize changes what fits, which changes whether there is anything to scroll to. */
    window.addEventListener('resize', () => paint(document), { passive: true });

    register(paint);
}
